import { PayloadAction } from '@reduxjs/toolkit';
import AcademyMaker from '@academy/maker';
import { IParentProcess } from '@/store/types';
import Message from '@/constant/message';
import { createSliceSteamup } from '../common';

export interface ThumbnailProps {
  main: string | File;
  images: (string | File)[];
}

export interface ClassRoomState {
  uuid: string;
  subject?: number[];
  grade?: number[];
  keywords: number[];
  price: number;
  level: number;
  title?: string;
  subTitle?: string;
  thumbnail?: ThumbnailProps;
  content?: string;
  inviteCode?: string;
  expiredAt?: number;
  isPost: boolean;
  policy: boolean; // TODO: 확인 필요
}

export interface ErrorsState {
  subject: string;
  grade: string;
  keywords: string;
  price: string;
  level: string;
  title: string;
  thumbnail: string;
  content: string;
  expiredAt: string;
  process: {
    [uuid: string]: string;
  };
  inviteCode: string;
  policy: string;
}

export type ActiveState = Record<
| 'subject'
| 'grade'
| 'keywords'
| 'price'
| 'level'
| 'title'
| 'thumbnail'
| 'content'
| 'expiredAt'
| 'process'
| 'inviteCode'
| 'policy',
boolean
>;

interface EditorState {
  splitMode?: boolean;
  editor1: AcademyMaker | null;
  editor2: AcademyMaker | null;
}

interface IClassRoomState {
  classroom: ClassRoomState;
  processData: IParentProcess[];
  loading: boolean;
  processing: boolean;
  errors: ErrorsState;
  active: ActiveState;
  currentLocationY: number;
  editor: EditorState;
  isEdit: boolean;
}

function createEmptyClassRoom(): ClassRoomState {
  return {
    uuid: '',
    subject: [],
    grade: [],
    price: -1,
    level: 0,
    title: '',
    subTitle: '',
    thumbnail: {
      main: '',
      images: [],
    },
    content: '',
    inviteCode: '',
    expiredAt: -1,
    isPost: false,
    keywords: [],
    policy: false,
  };
}

const initialErrorState = {
  subject: '',
  grade: '',
  keywords: '',
  price: '',
  level: '',
  title: '',
  thumbnail: '',
  content: '',
  expiredAt: '',
  process: {},
  // TODO: 확인 필요
  inviteCode: '',
  policy: '',
};

const initialActiveState = {
  subject: false,
  grade: false,
  keywords: false,
  price: false,
  level: false,
  title: false,
  thumbnail: false,
  content: false,
  expiredAt: false,
  process: false,
  // TODO: 확인 필요
  inviteCode: false,
  policy: false,
};

const initialState: IClassRoomState = {
  classroom: createEmptyClassRoom(),
  processData: [],
  loading: true,
  processing: false,
  editor: {
    editor1: null,
    editor2: null,
  },
  errors: { ...initialErrorState },
  active: { ...initialActiveState },
  currentLocationY: 0,
  isEdit: false,
};

const createClassRoomSlice = createSliceSteamup(
  'classRoom',
  initialState,
  {
    initClassRoomWithUUID(state, { payload }: PayloadAction<string>) {
      state.classroom = createEmptyClassRoom();
      state.classroom.uuid = payload;
    },
    setUUID(state, { payload }: PayloadAction<string>) {
      state.classroom.uuid = payload;
    },
    setSubject(state, { payload }: PayloadAction<number[]>) {
      state.classroom.subject = payload;
      createClassRoomSlice.caseReducers.validateSubject(state);
    },
    setGrade(state, { payload }: PayloadAction<number[]>) {
      state.classroom.grade = payload;
      createClassRoomSlice.caseReducers.validateGrade(state);
    },
    setPrice(state, { payload }: PayloadAction<number>) {
      state.classroom.price = payload;
      createClassRoomSlice.caseReducers.validatePrice(state);
    },
    setPolicy(state, { payload }: PayloadAction<boolean>) {
      state.classroom.policy = payload;
      createClassRoomSlice.caseReducers.validatePolicy(state);
    },
    setExpiredAt(state, { payload }: PayloadAction<number | undefined>) {
      state.classroom.expiredAt = payload;
      createClassRoomSlice.caseReducers.validateExpired(state);
    },
    setLevel(state, { payload }: PayloadAction<number>) {
      state.classroom.level = payload;
      createClassRoomSlice.caseReducers.validateLevel(state);
    },
    setTitle(state, { payload }: PayloadAction<string>) {
      state.classroom.title = payload;
      createClassRoomSlice.caseReducers.validateTitle(state);
    },
    setSubTitle(state, { payload }: PayloadAction<string>) {
      state.classroom.subTitle = payload;
      createClassRoomSlice.caseReducers.validateTitle(state);
    },
    setThumbnail(state, { payload }: PayloadAction<ThumbnailProps>) {
      state.classroom.thumbnail = payload;
      createClassRoomSlice.caseReducers.validateThumbnail(state);
    },
    setContent(state, { payload }: PayloadAction<string>) {
      state.classroom.content = payload;
      createClassRoomSlice.caseReducers.validateContent(state);
    },
    setInviteCode(state, { payload }: PayloadAction<string | undefined>) {
      state.classroom.inviteCode = payload;
    },
    setKeywords(state, { payload }: PayloadAction<number[]>) {
      state.classroom.keywords = payload;
    },
    // process
    initProcess(state) {
      state.processData = [...initialState.processData];
      createClassRoomSlice.caseReducers.validateProcess(state);
    },
    setProcessLectureTitle(state, { payload }: PayloadAction<{ processId: string, title: string }>) {
      state.processData.some((process) => {
        if (process.uuid === payload.processId) {
          process.title = payload.title;
          return true;
        }
        process.subProcessData?.some((childProcess) => {
          if (childProcess.uuid === payload.processId) {
            childProcess.title = payload.title;
            return true;
          }
          return false;
        });
        return false;
      });
      createClassRoomSlice.caseReducers.validateProcess(state);
    },
    setProcess(state, { payload }: PayloadAction<{ processData: IParentProcess[] }>) {
      state.processData = payload.processData;
      createClassRoomSlice.caseReducers.validateProcess(state);
    },
    setProcessEditor(state, { payload }: PayloadAction<{ editor: AcademyMaker, index: 0 | 1 }>) {
      const key: keyof EditorState = payload.index === 0 ? 'editor1' : 'editor2';
      return {
        ...state,
        editor: {
          ...state.editor,
          [key]: payload.editor,
        },
      };
    },
    setProcessEditorSplitMode(state, { payload }: PayloadAction<boolean>) {
      if (!payload && state.editor.editor2 !== null) state.editor.editor2 = null;
      state.editor.splitMode = payload;
      createClassRoomSlice.caseReducers.validateProcess(state);
    },
    clearProcessEditor(state) {
      state.editor.splitMode = undefined;
      state.editor.editor1 = null;
      state.editor.editor2 = null;
      createClassRoomSlice.caseReducers.validateProcess(state);
    },
    // errors -> 형식 확인
    validateSubject(state) {
      const { subject } = state.classroom;
      if (subject) {
        if (subject.length > 0) {
          state.errors.subject = '';
        } else { state.errors.subject = Message.강의분야는필수선택값입니다강의분야를선택해주시기바랍니다; }
      }
    },
    validateGrade(state) {
      const { grade } = state.classroom;
      if (grade) {
        if (grade.length > 0) {
          state.errors.grade = '';
        } else {
          state.errors.grade = Message.학년구분은필수선택값입니다학년구분을선택해주시기바랍니다;
        }
      }
    },
    validatePrice(state) {
      if (state.classroom.price !== -1) {
        state.errors.price = '';
      } else {
        state.errors.price = Message.수강료미설정;
      }
    },
    validateTitle(state) {
      if (!state.classroom.title) {
        state.errors.title = Message.강의제목은필수입력값입니다강의제목을입력해주시기바랍니다;
        return;
      }
      if (!state.classroom.subTitle) {
        state.errors.title = Message.부제목은필수입력값입니다부제목을입력해주시기바랍니다;
        return;
      }
      if (state.classroom.title.length > 70) {
        state.errors.title = Message.제목은70자이하로입력해주세요;
        return;
      }
      if (state.classroom.subTitle.length > 200) {
        state.errors.title = Message.부제목은200자이하로입력해주세요;
        return;
      }
      state.errors.title = '';
    },
    validateLevel(state) {
      if (state.classroom.level !== 0) {
        state.errors.level = '';
      } else { state.errors.level = Message.난이도는필수선택값입니다난이도를선택해주시기바랍니다; }
    },
    validateThumbnail(state) {
      if (state.classroom.thumbnail?.main) {
        state.errors.thumbnail = '';
      } else {
        state.errors.thumbnail = Message.커버이미지는필수업로드값입니다한개라도업로드해주시기바랍니다;
      }
    },
    validateContent(state) {
      const div = document.createElement('div');
      div.innerHTML = state.classroom.content ?? '';

      if (!div.innerText) {
        state.errors.content = Message.강의상세내용은필수입력값입니다강의상세내용을입력해주시기바랍니다;
      } else {
        state.errors.content = '';
      }
    },
    validateExpired(state) {
      if (state.classroom.expiredAt !== undefined) {
        if (state.classroom.expiredAt >= 0) {
          state.errors.expiredAt = '';
        } else {
          state.errors.expiredAt = Message.수강기한은필수선택값입니다수강기한을선택해주시기바랍니다;
        }
      }
    },
    validateProcess(state) {
      const errors: any = {};
      state.processData.forEach((pro, i) => {
        if (pro.title === '' || pro.title === undefined) {
          errors[pro.uuid] = `${i + 1}/${Message.$1강의제목을입력해주세요}`;
        } else if (pro.subProcessData === undefined || pro.subProcessData.length <= 0) {
          errors[pro.uuid] = `${i + 1}/${Message.$1강에활동을추가해주세요}`;
        }

        return pro.subProcessData.forEach((act, idx) => {
          if (act.title === '' || act.title === undefined) {
            errors[act.uuid] = `${idx + 1}/${Message.활동$1의제목을입력해주세요}`;
          } else if (act.contentSourceID === 0) {
            errors[act.uuid] = `${idx + 1}/${Message.활동$1을작성해주세요}`;
          }
        });
      });
      state.errors.process = errors;
    },
    // TODO: 번역 추가하기
    validatePolicy(state) {
      if (state.classroom.policy) {
        state.errors.policy = '';
      } else {
        state.errors.policy = Message.판매약관에동의해주세요;
      }
    },

    // check Active
    activeClassRoom(state) {
      if (state.classroom.subject?.length !== 0) {
        state.active.subject = true;
      } else { state.active.subject = false; }

      if (state.classroom.grade?.length !== 0) {
        state.active.grade = true;
      } else { state.active.grade = false; }

      if (state.classroom.title !== '' && state.classroom.subTitle !== ''
        && state.classroom.title !== undefined && state.classroom.subTitle !== undefined) {
        state.active.title = true;
      } else { state.active.title = false; }

      if (state.classroom.keywords.length !== 0 && state.classroom.keywords !== undefined) {
        state.active.keywords = true;
      } else { state.active.keywords = false; }

      if (state.classroom.price !== -1) {
        state.active.price = true;
      } else { state.active.price = false; }

      if (state.classroom.expiredAt !== -1) {
        state.active.expiredAt = true;
      } else { state.active.expiredAt = false; }

      if (state.classroom.level !== 0) {
        state.active.level = true;
      } else { state.active.level = false; }

      if (state.classroom.thumbnail?.main !== '' && state.classroom.thumbnail?.main !== undefined) {
        state.active.thumbnail = true;
      } else { state.active.thumbnail = false; }

      if (state.classroom.content !== '' && state.classroom.content !== undefined) {
        state.active.content = true;
      } else { state.active.content = false; }

      if (state.processData.length > 0
        && state.processData[0].title !== '' && state.processData[0].title !== undefined) {
        state.active.process = true;
      } else { state.active.process = false; }

      if (state.processData.length > 0) {
        state.processData.some((data) => {
          if (data.title === '' || data.title === undefined) {
            state.active.process = false;
            return true;
          } if (data.subProcessData === undefined || data.subProcessData.length < 1) {
            state.active.process = false;
            return true;
          }
          if (data.subProcessData) {
            data.subProcessData.forEach((act) => {
              if (act.title === '' || act.title === undefined) {
                state.active.process = false;
                return true;
              }
              state.active.process = true;
              return true;
            });
          }
          return false;
        });
      }

      if (state.classroom.inviteCode) {
        state.active.inviteCode = true;
      } else { state.active.inviteCode = false; }

      if (state.classroom.policy) {
        state.active.policy = true;
      } else { state.active.policy = false; }
    },

  },
);

export default createClassRoomSlice;
